import { useNostr } from '@nostrify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { toast } from '@/hooks/useToast';
import { DELETION_KIND } from '@/lib/nip25';
import { HIGHLIGHT_KIND, buildHighlightTags, isValidHighlight, type HighlightTarget } from '@/lib/nip84';

interface HighlightsResult {
  /** Active (non-retracted) public highlights on this event, newest first. */
  highlights: NostrEvent[];
}

/**
 * Fetches NIP-84 highlights (`kind:9802`) tagging a given event, honoring
 * NIP-09 deletion requests as retractions. Unlike stars, highlights are
 * not deduplicated per author — the same reader may highlight several
 * different (or even overlapping) passages, and each is its own public
 * highlight.
 */
export function useHighlights(target: HighlightTarget | undefined) {
  const { nostr } = useNostr();

  return useQuery<HighlightsResult>({
    queryKey: ['highlights', target?.id],
    queryFn: async (c) => {
      if (!target) return { highlights: [] };
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query(
        [{ kinds: [HIGHLIGHT_KIND], '#e': [target.id], limit: 500 }],
        { signal },
      );
      const valid = events.filter(isValidHighlight);
      if (valid.length === 0) return { highlights: [] };

      // Honor NIP-09 deletion requests retracting a highlight. Only a
      // deletion authored by the same pubkey as the highlight it
      // references counts as valid.
      const deletions = await nostr.query(
        [{ kinds: [DELETION_KIND], '#e': valid.map((h) => h.id), limit: 500 }],
        { signal },
      );
      const deletedIds = new Set(
        valid
          .filter((h) =>
            deletions.some(
              (d) => d.pubkey === h.pubkey && d.tags.some(([n, v]) => n === 'e' && v === h.id),
            ),
          )
          .map((h) => h.id),
      );

      const active = valid.filter((h) => !deletedIds.has(h.id));
      active.sort((a, b) => b.created_at - a.created_at);
      return { highlights: active };
    },
    enabled: Boolean(target?.id),
    staleTime: 30_000,
  });
}

/** Publishes a NIP-84 highlight (`kind:9802`, `content` = the highlighted text) on `target`. */
export function useCreateHighlight(target: HighlightTarget) {
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ content, context }: { content: string; context?: string }) => {
      return publishEvent({
        kind: HIGHLIGHT_KIND,
        content,
        tags: buildHighlightTags(target, context),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['highlights', target.id] });
    },
    onError: () => {
      toast({ title: 'Failed to save highlight.', variant: 'destructive' });
    },
  });
}

/** Retracts (NIP-09 deletion) one of the current user's own highlights. */
export function useDeleteHighlight(target: HighlightTarget) {
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (highlight: NostrEvent) => {
      if (!user || highlight.pubkey !== user.pubkey) {
        throw new Error('You can only remove your own highlights.');
      }
      return publishEvent({
        kind: DELETION_KIND,
        content: '',
        tags: [
          ['e', highlight.id],
          ['k', String(HIGHLIGHT_KIND)],
        ],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['highlights', target.id] });
      toast({ title: 'Highlight removed' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove highlight',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
